import {useFlags} from 'launchdarkly-react-client-sdk';
import {useMemo} from 'react';
import {CampaignTab} from '@/types/campaignTab';
import {CAMPAIGN_TAB_ID, CampaignTabId} from '@/types/campaignTabId';
import useDashboardUserQuery from '@/hooks/queries/use-dashboard-user-query';

/**
 * Campaign tab IDs that are allowed for non-admin users when fifaRevamp flag is enabled.
 * Non-admin users will only see these tabs: Brief, Applications, Participants, Stats.
 */
const ALLOWED_CAMPAIGN_TAB_IDS_FOR_FIFA_REVAMP_NON_ADMIN: CampaignTabId[] = [
  CAMPAIGN_TAB_ID.BRIEF,
  CAMPAIGN_TAB_ID.PROPOSALS,
  CAMPAIGN_TAB_ID.CONTRACTS,
  CAMPAIGN_TAB_ID.STATS,
];

interface UseFilterCampaignTabsForFifaRevampProps {
  tabs: CampaignTab[];
}

interface UseFilterCampaignTabsForFifaRevampResult {
  filteredTabs: CampaignTab[];
}

/**
 * Hook that filters campaign tabs based on fifaRevamp flag and user role.
 * - If user is admin (CSM) - returns all tabs unchanged
 * - If fifaRevamp flag is enabled and user is NOT admin - returns only:
 *   Brief, Applications, Participants, Stats
 */
function useFilterCampaignTabsForFifaRevamp({
  tabs,
}: UseFilterCampaignTabsForFifaRevampProps): UseFilterCampaignTabsForFifaRevampResult {
  const {dashboardUser} = useDashboardUserQuery({});
  const flags = useFlags();

  return useMemo(() => {
    // If user is admin (CSM), return all tabs unchanged
    if (dashboardUser.hasPlatformAdminRole) {
      return {filteredTabs: tabs};
    }

    // If fifaRevamp flag is not enabled, return all tabs unchanged
    if (!flags.fifaRevamp) {
      return {filteredTabs: tabs};
    }

    // For non-admin users with fifaRevamp flag, filter to only show allowed tabs
    const filteredTabs = tabs.filter((tab) =>
      ALLOWED_CAMPAIGN_TAB_IDS_FOR_FIFA_REVAMP_NON_ADMIN.includes(tab.id)
    );

    return {filteredTabs};
  }, [tabs, flags.fifaRevamp, dashboardUser.hasPlatformAdminRole]);
}

export default useFilterCampaignTabsForFifaRevamp;
export type {UseFilterCampaignTabsForFifaRevampResult};
